import { memo, useCallback } from 'react';

import { useTrackClickedAddContent } from '@air/analytics';
import { BoardIcon, UploadIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { ContributableNullState } from '~/components/ContributableNullState/ContributableNullState';
import { ContributableNullStateActions } from '~/components/ContributableNullState/ContributableNullStateActions';
import { NonContributableEmptyState } from '~/components/NonContributableNullState/NonContributableNullState';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useCurrentBoardPermissions } from '~/hooks/useCurrentBoardPermissions';
import { useUploadFilesFromComputer } from '~/hooks/useUploadFilesFromComputer';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
// eslint-disable-next-line no-restricted-imports -- PLA-1724 — pre-existing debt, not introduced by the icon migration
import { PrivateBoardNoSearchResults } from './PrivateBoardNoSearchResults';
// eslint-disable-next-line no-restricted-imports -- PLA-1724 — pre-existing debt, not introduced by the icon migration
import { usePrivateBoardActions } from './usePrivateBoardActions';

const DEFAULT_TITLE = 'No content yet';
const DEFAULT_DESCRIPTION = 'Assets and boards you add to this board will appear here.';

export const PrivateBoardNullState = memo(() => {
  const trackLocation = 'board-container-null-state';
  const { uploadFilesFromComputer } = useUploadFilesFromComputer({ trackLocation });
  const { boardPermissions } = useCurrentBoardPermissions();
  const canAddSubboard = canCreateBoard(boardPermissions);
  const canAddAssets = canCreateAsset(boardPermissions);
  const { createOptions, uploadOptions } = usePrivateBoardActions({ trackLocation });
  const { isSearchActive } = useIsSearchActive();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { trackClickedAddContent } = useTrackClickedAddContent();

  const handleActionsOpenChange = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded) {
        trackClickedAddContent({ location: trackLocation });
      }
    },
    [trackClickedAddContent],
  );

  // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
  if (isSearchActive) return <PrivateBoardNoSearchResults />;

  if (isAboveMediumScreen && (canAddAssets || canAddSubboard)) {
    return (
      <ContributableNullState
        data-testid="PRIVATE_BOARD_NULL_STATE"
        icon={BoardIcon}
        title={DEFAULT_TITLE}
        description={DEFAULT_DESCRIPTION}
        cta={
          <ContributableNullStateActions
            createOptions={createOptions}
            uploadOptions={uploadOptions}
            onOpenChange={handleActionsOpenChange}
          />
        }
      />
    );
  }

  if (!isAboveMediumScreen && canAddSubboard) {
    return (
      <ContributableNullState
        icon={BoardIcon}
        title={DEFAULT_TITLE}
        description={DEFAULT_DESCRIPTION}
        cta={
          <Button
            prefix={<UploadIcon size="sm" />}
            data-testid="CONTRIBUTABLE_NULL_STATE_UPLOAD_BUTTON"
            appearance="filled"
            color="blue"
            size="medium"
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => {
              uploadFilesFromComputer();
              trackClickedAddContent({ location: trackLocation });
            }}
          >
            Upload content
          </Button>
        }
      />
    );
  }

  return <NonContributableEmptyState icon={BoardIcon} />;
});

PrivateBoardNullState.displayName = 'PrivateBoardNullState';
